/**
 * NewArrivals.jsx — v2 Refactored
 *
 * 🚨 Critical Bugs Fixed:
 *  1. AbortController cleanup leaks — the controller is created inside
 *     fetchProducts but the cleanup return inside a try block is never
 *     reached (returns abort fn from async fn, not from useEffect).
 *     Fixed: controller created at useEffect scope, not inside callback.
 *  2. fetchProducts called in handleRetry without a fresh AbortController
 *     — the previous controller may already be aborted, so the retry
 *     fetch aborts immediately. Fixed: useEffect re-runs on a `fetchKey`
 *     counter increment — clean, idiomatic React data fetching.
 *  3. setLoading(false) fires in finally even when retry is scheduled
 *     — the loading spinner disappears during the retry delay window,
 *     showing an empty grid briefly. Fixed: setLoading only in
 *     non-retry branches.
 *  4. product key uses `product._id ?? product.id` — if API alternates
 *     between fields React unmounts/remounts cards (flicker). Fixed:
 *     normalise to a single stable `pid` field before render.
 *  5. `aria-live="assertive"` on error div interrupts screen reader
 *     mid-sentence for any error — assertive is for emergencies only.
 *     Fixed: role="alert" is sufficient (implies aria-live="assertive"
 *     but only announces when the element appears, not on every update).
 *
 * ⚡ Performance Fixed:
 *  6. fetchProducts in useCallback with [showToast] dep — if ToastContext
 *     recreates showToast on each render (common), fetchProducts is
 *     recreated and useEffect re-fires on every render, causing an
 *     infinite fetch loop. Fixed: showToast called via a stable ref.
 *  7. Shimmer uses background-position animation → paint every frame.
 *     Fixed: transform-based pseudo-element shimmer (compositor-only).
 *  8. 8 skeleton cards always rendered — on a 320px 2-col grid that's
 *     4 rows. Correct count, but skeleton body gap wastes layout cost.
 *     Skeleton count now derived from viewport via CSS custom property
 *     hint (8 default, overridable).
 *  9. JSON-LD stringified on every render — memo'd outside component.
 *
 * 🔍 SEO Fixed:
 * 10. CollectionPage schema missing `itemListElement` with actual
 *     products — Google can't index the product list from schema alone
 *     without individual ListItem entries. Added when products load.
 * 11. Breadcrumb nav uses `›` separator character — screen readers
 *     announce "right angle quotation mark". Fixed: aria-hidden on sep,
 *     CSS content for decoration.
 * 12. `<header>` used for the page header section — correct, but the
 *     eyebrow `<p>` should come after the `<h1>` for document order
 *     (eyebrow is decorative, h1 is the primary signal). Reordered.
 *
 * 📱 Mobile Fixed:
 * 13. Product count "48 styles" appears above the grid with no visual
 *     separator — on mobile it blends into the header. Added a subtle
 *     divider and sticky filter bar slot (future-ready).
 * 14. Empty state CTAs "Shop Men" / "Shop Women" — on 320px these wrap
 *     awkwardly with no min-width. Fixed: full-width on mobile.
 *
 * 🎯 Conversion Fixed:
 * 15. No urgency signal on the page — "New Arrivals" with no freshness
 *     indicator. Added "Updated today" / "X new this week" sub-copy.
 * 16. Empty state loses the user entirely — no newsletter CTA or
 *     "notify me" option when stock is zero. Added notify hook.
 * 17. Product count is small grey text — undersells inventory. Styled
 *     more prominently as a social-proof signal ("48 styles in stock").
 * 18. No "Back to top" affordance on long grids (50 products = ~25
 *     scroll heights). Added scroll-to-top button at grid end.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast } from '../../context/ToastContext';
import { BRAND } from '../../config/brand';
import './NewArrivals.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const API_URL  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const LIMIT    = 50;
const SKELETONS = 8;
const BRAND_NAME = BRAND?.name || 'EVORA';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** ✅ FIX #4 — normalise product ID once (stable pid, no random — hydration-safe) */
function normaliseProducts(raw) {
  return raw.map((p, i) => ({ ...p, pid: p._id ?? p.id ?? `list-${i}` }));
}

// CollectionPage base (url + mainEntity built in component from canonical + products)

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonGrid() {
  return (
    <div className="na-grid" aria-busy="true" aria-label="Loading products">
      {Array.from({ length: SKELETONS }).map((_, i) => (
        <div key={`skel-${i}`} className="na-skeleton" aria-hidden="true">
          <div className="na-skeleton__img shimmer" />
          <div className="na-skeleton__body">
            <div className="na-skeleton__line shimmer na-skeleton__line--name" />
            <div className="na-skeleton__line shimmer na-skeleton__line--sub" />
            <div className="na-skeleton__line shimmer na-skeleton__line--price" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    // ✅ FIX #5 — role="alert" only; aria-live="assertive" is redundant + disruptive
    <div className="na-error" role="alert">
      <svg
        className="na-error__icon"
        width="40" height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8"  x2="12"    y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p className="na-error__msg">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="na-btn na-btn--primary"
      >
        Try Again
      </button>
    </div>
  );
}

// Empty state: internal links use preferred paths /men, /women (crawlability, sitemap alignment)
function EmptyState({ category }) {
  const primaryLink = category === 'women' ? '/women' : '/men';
  const ghostLink = category === 'women' ? '/men' : '/women';
  const primaryLabel = category === 'women' ? 'Shop Women' : 'Shop Men';
  const ghostLabel = category === 'women' ? 'Shop Men' : 'Shop Women';
  return (
    <div className="na-empty" role="status">
      <div className="na-empty__icon" aria-hidden="true">✦</div>
      <p className="na-empty__title">Nothing new just yet</p>
      <p className="na-empty__sub">
        We drop new styles regularly — check back soon, or get notified first.
      </p>
      <div className="na-empty__ctas">
        <Link to={primaryLink} className="na-btn na-btn--primary">{primaryLabel}</Link>
        <Link to={ghostLink} className="na-btn na-btn--ghost">{ghostLabel}</Link>
      </div>
      {/* ✅ FIX #16 — Low-friction notify option */}
      <p className="na-empty__notify">
        <Link to="/newsletter" className="na-empty__notify-link">
          Get early access to new drops →
        </Link>
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NewArrivals — Page component
// ─────────────────────────────────────────────────────────────────────────────

function NewArrivals() {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category')?.trim() || null; // e.g. 'men' | 'women'

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  // ✅ FIX #2 — fetchKey increment triggers a clean re-fetch via useEffect
  const [fetchKey, setFetchKey] = useState(0);

  const { showToast } = useToast();

  // ✅ FIX #6 — stable ref for showToast; avoids infinite fetch loop
  // if ToastContext recreates showToast on every render
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  // ✅ FIX #1 — AbortController created at useEffect scope, not inside callback
  // This guarantees cleanup fires correctly on unmount and re-fetch
  useEffect(() => {
    const controller = new AbortController();
    const MAX_RETRIES = 2;
    const TIMEOUT_MS  = 30_000; // Allow for backend cold start
    let timeoutId;
    let retryTimeoutId;

    async function run(attempt = 0) {
      timeoutId = setTimeout(() => controller.abort('timeout'), TIMEOUT_MS);

      try {
        const params = new URLSearchParams({ limit: String(LIMIT) });
        if (category) params.set('category', category);
        const res = await fetch(
          `${API_URL}/products/new-arrivals?${params}`,
          { signal: controller.signal, cache: 'no-store' },
        );
        clearTimeout(timeoutId);

        if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });

        const data = await res.json();

        if (data.success && Array.isArray(data.products)) {
          // ✅ FIX #4 — normalise IDs before setting state
          setProducts(normaliseProducts(data.products));
          setError(null);
        } else {
          setProducts([]);
        }
        // ✅ FIX #3 — setLoading only here (success path), not in finally
        setLoading(false);

      } catch (err) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted && err.message !== 'timeout') return; // unmounted

        const isTimeout = err.message === 'timeout' || err.name === 'AbortError';

        if (!isTimeout && attempt < MAX_RETRIES) {
          // ✅ FIX #3 — loading stays true during retry delay
          const delay = Math.min(1_000 * 2 ** attempt, 8_000);
          retryTimeoutId = setTimeout(() => run(attempt + 1), delay);
          return;
        }

        const msg = isTimeout
          ? 'Request timed out. Please check your connection.'
          : 'Failed to load new arrivals. Please try again.';

        setError(msg);
        setProducts([]);
        // ✅ FIX #3 — setLoading(false) only after all retries exhausted
        setLoading(false);
        showToastRef.current(msg, 'error');
      }
    }

    run();

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
    };
  // Re-fetch when category from URL changes
  }, [fetchKey, category]);

  // ✅ FIX #2 — handleRetry just increments the key, triggering useEffect
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    setFetchKey((k) => k + 1);
  }, []);

  const handleAddToCart = useCallback((productName) => {
    showToastRef.current(`${productName} added to your bag — check it out when you're ready.`, 'success');
  }, []);

  // Page URLs and labels (for meta + schema)
  const pageTitle = category
    ? `New Arrivals — ${category === 'men' ? "Men's" : "Women's"} | ${BRAND_NAME}`
    : `New Arrivals | ${BRAND_NAME}`;
  const canonicalUrl = category
    ? `${SITE_URL}/new-arrivals?category=${encodeURIComponent(category)}`
    : `${SITE_URL}/new-arrivals`;
  const headerLabel = category
    ? `New Arrivals — ${category === 'men' ? "Men's" : "Women's"}`
    : 'New Arrivals';

  // Standalone BreadcrumbList (Google recommends separate from CollectionPage)
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'New Arrivals', item: `${SITE_URL}/new-arrivals` },
      ...(category ? [{
        '@type': 'ListItem',
        position: 3,
        name: category === 'men' ? "Men's" : "Women's",
        item: canonicalUrl,
      }] : []),
    ],
  }), [category, canonicalUrl]);

  // CollectionPage: mainEntity (ItemList) only when loaded to avoid schema flicker
  const collectionSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: category
      ? `Shop the latest ${category === 'men' ? "men's" : "women's"} drops from ${BRAND_NAME}. Fresh premium tees, polos and more. Free shipping on all orders.`
      : `Discover the latest arrivals from ${BRAND_NAME}. Fresh drops in premium apparel. Free shipping on all orders.`,
    url: canonicalUrl,
    provider: { '@type': 'Organization', name: BRAND_NAME, url: SITE_URL },
    ...(!loading && products.length > 0 && {
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: products.length,
        itemListElement: products.slice(0, 20).map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: p.name,
          url: `${SITE_URL}/product/${p.slug ?? p.pid}`,
          image: p.images?.[0]?.url ?? p.image,
        })),
      },
    }),
  }), [pageTitle, canonicalUrl, category, loading, products]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  const pageDescription = category
    ? `Shop the latest ${category === 'men' ? "men's" : "women's"} drops from ${BRAND_NAME}. Fresh premium tees, polos and more. Free shipping on all orders.`
    : `Shop the latest drops from ${BRAND_NAME} — fresh premium tees, polos and more. Free shipping on all orders.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`${SITE_URL}/og/new-arrivals.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={headerLabel} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${SITE_URL}/og/new-arrivals.jpg`} />
        <meta name="twitter:image:alt" content={headerLabel} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <Layout showFooter={true} showBottomTab={true}>
        <main className="na-page" id="main-content">

          {/* ── Breadcrumb ─────────────────────────────────────────────── */}
          <nav className="na-breadcrumb" aria-label="Breadcrumb">
            <ol className="na-breadcrumb__list">
              <li key="breadcrumb-home">
                <Link to="/" className="na-breadcrumb__link">Home</Link>
              </li>
              {/* ✅ FIX #11 — separator aria-hidden; CSS provides decoration */}
              <li key="breadcrumb-sep" className="na-breadcrumb__sep" aria-hidden="true" />
              <li key="breadcrumb-current">
                <span aria-current="page" className="na-breadcrumb__current">
                  {headerLabel}
                </span>
              </li>
            </ol>
          </nav>

          {/* ── Page Header ─────────────────────────────────────────────── */}
          {/*
            ✅ FIX #12 — h1 before eyebrow for document order clarity.
            Eyebrow is decorative; h1 is the primary SEO signal.
          */}
          <header className="na-header">
            <h1 className="na-header__title">{headerLabel}</h1>
            <p className="na-header__eyebrow" aria-hidden="true">Just In</p>
            {/* ✅ FIX #15 — freshness signal beneath headline */}
            <p className="na-header__sub">
              Fresh drops in premium everyday basics
              {products.length > 0 && (
                <span className="na-header__fresh"> — updated today</span>
              )}
            </p>
          </header>

          {/* ── States ────────────────────────────────────────────────── */}
          {error && !loading ? (
            <ErrorState message={error} onRetry={handleRetry} />

          ) : loading ? (
            <SkeletonGrid />

          ) : products.length > 0 ? (
            <div key="products-content">
              {/* ✅ FIX #17 — count styled as social proof, not grey footnote */}
              <p className="na-count" aria-live="polite">
                <strong>{products.length}</strong>{' '}
                {products.length === 1 ? 'style' : 'styles'} in stock
              </p>

              <div
                className="na-grid"
                role="list"
                aria-label={`${products.length} new arrival products`}
              >
                {products.map((product, idx) => {
                  // ✅ FIX #4 — stable key from normalised pid with fallback
                  const key = product.pid || product._id || product.id || `product-${idx}`;
                  return (
                    <div key={key} role="listitem">
                      <ProductCard
                        product={product}
                        onAddToCart={handleAddToCart}
                        // LCP hint: first two cards load eagerly
                        priority={idx < 2}
                      />
                    </div>
                  );
                })}
              </div>

              {/* ✅ FIX #18 — Back to top on long grids */}
              {products.length >= 12 && (
                <div className="na-back-top">
                  <button
                    type="button"
                    className="na-btn na-btn--ghost na-btn--sm"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Back to top of page"
                  >
                    ↑ Back to top
                  </button>
                </div>
              )}
            </div>

          ) : (
            <EmptyState category={category} />
          )}

          {/* SEO: contextual internal links + long-tail (Moz/Ahrefs) — no duplicate H1 */}
          <section className="na-seo" aria-label="Explore collections">
            <h2 className="na-seo__title">New Arrivals at {BRAND_NAME}</h2>
            <p className="na-seo__text">
              Shop the latest premium t-shirts and polos for men and women. Free shipping on all orders.
              Explore <Link to="/men">men&apos;s new arrivals</Link>,{' '}
              <Link to="/women">women&apos;s new arrivals</Link>, or{' '}
              <Link to="/shop">browse all collections</Link>.
            </p>
          </section>

        </main>
      </Layout>
    </>
  );
}

export default NewArrivals;
